import {
  doc,
  updateDoc,
  collection,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { createMaintenancePaymentSale } from './posService';
import { db } from '../config/firebase';

// دالة مساعدة للحصول على Firestore
const ensureFirestore = () => {
  if (!db) {
    throw new Error('Firestore database is not initialized');
  }
  return db;
};
import { 
  MaintenanceRecord, 
  PaymentTransaction, 
  MaintenancePaymentMethod, 
  PaymentStatus 
} from '../utils/types';

/**
 * خدمة إدارة التكلفة والدفع في الصيانة
 * تتبع التدفق الطبيعي: تكلفة مبدئية -> تقدير -> تكلفة نهائية -> دفع
 */

/**
 * 1. تسجيل التكلفة المبدئية عند استلام الجهاز
 */
export const setInitialCost = async (
  maintenanceId: string,
  initialCost: number,
  userId: string
): Promise<void> => {
  try {
    const maintenanceRef = doc(db, 'maintenance', maintenanceId);
    
    await updateDoc(maintenanceRef, {
      initialCost,
      estimatedCost: initialCost, // نفس القيمة في البداية
      totalPaid: 0,
      remainingAmount: initialCost,
      paymentStatus: 'unpaid' as PaymentStatus,
      hasDebt: false,
      requireCallBeforeMaintenance: false,
      customerNotified: false,
      customerApproved: false,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
    
    console.log(`Initial cost set to ${initialCost} for maintenance ${maintenanceId}`);
  } catch (error) {
    console.error('Error setting initial cost:', error);
    throw error;
  }
};

/**
 * 2. تحديث التكلفة المقدرة بعد الفحص
 */
export const updateEstimatedCost = async (
  maintenanceId: string,
  estimatedCost: number,
  requireCallBeforeMaintenance: boolean,
  userId: string,
  notes?: string
): Promise<void> => {
  try {
    const maintenanceRef = doc(db, 'maintenance', maintenanceId);
    
    // الحصول على البيانات الحالية
    const maintenanceDoc = await getDoc(maintenanceRef);
    if (!maintenanceDoc.exists()) {
      throw new Error('Maintenance record not found');
    }
    
    const currentData = maintenanceDoc.data();
    const currentPaid = currentData.totalPaid || 0;
    
    await updateDoc(maintenanceRef, {
      estimatedCost,
      remainingAmount: Math.max(0, estimatedCost - currentPaid),
      requireCallBeforeMaintenance,
      customerNotified: requireCallBeforeMaintenance, // إذا طلب الاتصال، فقد تم إشعاره
      customerApproved: !requireCallBeforeMaintenance, // إذا لم يطلب الاتصال، فهو موافق
      updatedAt: Timestamp.now(),
      updatedBy: userId,
      ...(notes && { estimationNotes: notes })
    });
    
    console.log(`Estimated cost updated to ${estimatedCost} for maintenance ${maintenanceId}`);
  } catch (error) {
    console.error('Error updating estimated cost:', error);
    throw error;
  }
};

/**
 * 3. تأكيد موافقة العميل على التكلفة
 */
export const confirmCustomerApproval = async (
  maintenanceId: string,
  approved: boolean,
  finalCost?: number,
  userId?: string
): Promise<void> => {
  try {
    const maintenanceRef = doc(db, 'maintenance', maintenanceId);

    const updateData: any = {
      customerApproved: approved,
      customerNotified: true,
      updatedAt: Timestamp.now()
    };

    if (userId) {
      updateData.updatedBy = userId;
    }

    // إذا وافق العميل
    if (approved) {
      // إذا تم تحديد تكلفة نهائية
      if (finalCost !== undefined) {
        const maintenanceDoc = await getDoc(maintenanceRef);
        if (maintenanceDoc.exists()) {
          const currentData = maintenanceDoc.data();
          const currentPaid = currentData.totalPaid || currentData.paid || 0;

          updateData.finalCost = finalCost;
          updateData.remainingAmount = Math.max(0, finalCost - currentPaid);
        }
      }

      // استئناف الصيانة - تغيير الحالة إلى "قيد التنفيذ"
      updateData.status = 'inProgress';
    } else {
      // إذا رفض العميل - إلغاء الطلب
      updateData.status = 'cancelled';
      updateData.cancelledAt = Timestamp.now();
      updateData.cancellationReason = 'رفض العميل للتكلفة المقدرة';
    }

    await updateDoc(maintenanceRef, updateData);

    console.log(`Customer approval ${approved ? 'confirmed' : 'rejected'} for maintenance ${maintenanceId}`);
  } catch (error) {
    console.error('Error confirming customer approval:', error);
    throw error;
  }
};

/**
 * 4. تحديد التكلفة النهائية عند التسليم
 */
export const updateMaintenanceFinalCost = async (
  maintenanceId: string,
  finalCost: number,
  userId: string,
  partsCost?: number,
  paymentMethod?: MaintenancePaymentMethod
): Promise<void> => {
  try {
    const maintenanceRef = doc(db, 'maintenance', maintenanceId);

    // الحصول على البيانات الحالية
    const maintenanceDoc = await getDoc(maintenanceRef);
    if (!maintenanceDoc.exists()) {
      throw new Error('Maintenance record not found');
    }

    const currentData = maintenanceDoc.data();
    const currentPaid = currentData.totalPaid || currentData.paid || 0;
    const calculatedPartsCost = partsCost !== undefined ? partsCost :
      (currentData.usedParts || []).reduce((sum: number, part: any) =>
        sum + ((part.sellingPrice || part.purchasePrice || 0) * part.quantity), 0);
    const laborCost = Math.max(0, finalCost - calculatedPartsCost);

    const updateData: any = {
      finalCost,
      partsCost: calculatedPartsCost,
      laborCost,
      remainingAmount: Math.max(0, finalCost - currentPaid),
      // تحديث الحقول القديمة للتوافق
      maintenanceFee: laborCost,
      technicianShare: Math.max(0, currentPaid - calculatedPartsCost),
      // تحديث حالة الصيانة إلى "تم التسليم" عند تحديد التكلفة النهائية
      status: 'delivered',
      deliveredAt: Timestamp.now(),
      actualDeliveryDate: Timestamp.now(),
      updatedAt: Timestamp.now(),
      updatedBy: userId
    };

    // إضافة طريقة الدفع إذا تم تحديدها
    if (paymentMethod) {
      updateData.paymentMethod = paymentMethod;
    }

    await updateDoc(maintenanceRef, updateData);

    console.log(`Final cost set to ${finalCost} for maintenance ${maintenanceId}`);
  } catch (error) {
    console.error('Error setting final cost:', error);
    throw error;
  }
};

// Alias للتوافق مع الاستخدامات القديمة
export const setFinalCost = updateMaintenanceFinalCost;

/**
 * 8. تسديد دفعة بعد التسليم
 */
export const recordPostDeliveryPayment = async (
  maintenanceId: string,
  amount: number,
  method: MaintenancePaymentMethod,
  userId: string,
  userName: string,
  notes?: string
): Promise<PaymentTransaction> => {
  try {
    // الحصول على بيانات الصيانة الحالية
    const maintenanceRef = doc(db, 'maintenance', maintenanceId);
    const maintenanceDoc = await getDoc(maintenanceRef);

    if (!maintenanceDoc.exists()) {
      throw new Error('Maintenance record not found');
    }

    const maintenanceData = maintenanceDoc.data();

    // التحقق من أن الطلب تم تسليمه
    if (maintenanceData.status !== 'delivered') {
      throw new Error('Cannot record payment for non-delivered maintenance');
    }

    const currentPaid = maintenanceData.totalPaid || maintenanceData.paid || 0;
    const finalCost = maintenanceData.finalCost || maintenanceData.estimatedCost || maintenanceData.initialCost;
    const remainingAmount = Math.max(0, finalCost - currentPaid);

    // التحقق من أن المبلغ لا يتجاوز المبلغ المتبقي
    if (amount > remainingAmount) {
      throw new Error(`Payment amount (${amount}) exceeds remaining amount (${remainingAmount})`);
    }

    const partsCost = maintenanceData.partsCost || 0;

    // حساب القيم الجديدة
    const newTotalPaid = currentPaid + amount;
    const newRemainingAmount = Math.max(0, finalCost - newTotalPaid);

    // تحديد حالة الدفع
    let paymentStatus: PaymentStatus;
    if (newTotalPaid >= finalCost) {
      paymentStatus = 'fullyPaid';
    } else if (newTotalPaid > 0) {
      paymentStatus = 'partiallyPaid';
    } else {
      paymentStatus = 'unpaid';
    }

    // إنشاء سجل المعاملة
    const transaction: Omit<PaymentTransaction, 'id'> = {
      maintenanceId,
      amount,
      method,
      date: new Date(),
      notes: notes || `دفعة بعد التسليم - ${method === 'cash' ? 'نقدي' : 'دين'}`,
      createdBy: userId,
      createdByName: userName
    };

    // حفظ المعاملة
    const transactionRef = await addDoc(collection(db, 'paymentTransactions'), {
      ...transaction,
      createdAt: Timestamp.now()
    });

    // تحديث سجل الصيانة
    const updateData: any = {
      totalPaid: newTotalPaid,
      remainingAmount: newRemainingAmount,
      paymentStatus,
      lastPaymentDate: Timestamp.now(),
      lastPaymentMethod: method,
      // تحديث الحقول القديمة للتوافق
      paid: newTotalPaid,
      technicianShare: Math.max(0, newTotalPaid - partsCost),
      updatedAt: Timestamp.now(),
      updatedBy: userId
    };

    // إذا تم السداد بالكامل، إزالة معلومات الدين
    if (paymentStatus === 'fullyPaid') {
      updateData.hasDebt = false;
      updateData.debtAmount = 0;

      // تحديث سجل الدين إذا كان موجوداً
      if (maintenanceData.debtId) {
        const debtRef = doc(db, 'debts', maintenanceData.debtId);
        await updateDoc(debtRef, {
          status: 'paid',
          paidAmount: maintenanceData.debtAmount || newRemainingAmount + amount,
          remainingAmount: 0,
          paidAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
      }
    } else {
      // تحديث مبلغ الدين
      updateData.hasDebt = newRemainingAmount > 0;
      updateData.debtAmount = newRemainingAmount;

      // تحديث سجل الدين إذا كان موجوداً
      if (maintenanceData.debtId && newRemainingAmount > 0) {
        const debtRef = doc(db, 'debts', maintenanceData.debtId);
        await updateDoc(debtRef, {
          paidAmount: (maintenanceData.debtAmount || remainingAmount) - newRemainingAmount,
          remainingAmount: newRemainingAmount,
          status: 'partially_paid',
          updatedAt: Timestamp.now()
        });
      }
    }

    await updateDoc(maintenanceRef, updateData);

    // إذا كانت الدفعة نقدية، تسجيلها في الخزينة كدفعة صيانة نقدية (منفصلة عن المبيعات)
    if (method === 'cash') {
      try {
        await addDoc(collection(db, 'drawerTransactions'), {
          type: 'maintenance_cash_payment',
          amount: amount, // موجب - يزيد الخزينة
          description: `دفعة صيانة نقدية بعد التسليم - ${maintenanceData.customerName}`,
          maintenanceId,
          customerId: maintenanceData.customerId || '',
          customerName: maintenanceData.customerName || 'عميل غير محدد',
          employeeId: userId,
          employeeName: userName,
          branchId: maintenanceData.branchId || 'default',
          branchName: maintenanceData.branchName || 'الفرع الرئيسي',
          date: new Date().toISOString().split('T')[0],
          paymentDate: new Date(),
          notes: notes || `دفعة صيانة بعد التسليم - ${maintenanceId.substring(0, 8)}`,
          createdAt: serverTimestamp()
        });
        console.log(`✅ تم تسجيل الدفعة النقدية بعد التسليم في الخزينة كدفعة صيانة للصيانة ${maintenanceId}`);
      } catch (treasuryError) {
        console.error('❌ خطأ في تسجيل الدفعة في الخزينة:', treasuryError);
        // لا نريد إلغاء العملية إذا فشل تسجيل الخزينة
      }
    }

    console.log(`Post-delivery payment of ${amount} recorded for maintenance ${maintenanceId}`);

    return {
      id: transactionRef.id,
      ...transaction
    };
  } catch (error) {
    console.error('Error recording post-delivery payment:', error);
    throw error;
  }
};

/**
 * 9. الحصول على معلومات الدفعات المتأخرة
 */
export const getOutstandingPaymentInfo = (maintenance: MaintenanceRecord) => {
  const finalCost = maintenance.finalCost || maintenance.estimatedCost || maintenance.initialCost || 0;
  const totalPaid = maintenance.totalPaid || maintenance.paid || 0;
  const remainingAmount = Math.max(0, finalCost - totalPaid);

  const isDelivered = maintenance.status === 'delivered';
  const hasOutstandingPayment = remainingAmount > 0;
  const hasDebt = maintenance.hasDebt || false;

  return {
    finalCost,
    totalPaid,
    remainingAmount,
    isDelivered,
    hasOutstandingPayment,
    hasDebt,
    canAcceptPayment: isDelivered && hasOutstandingPayment,
    paymentStatus: maintenance.paymentStatus,
    lastPaymentDate: maintenance.paymentDate,
    lastPaymentMethod: maintenance.paymentMethod,
    debtId: maintenance.debtId,
    debtDueDate: maintenance.debtDueDate
  };
};

/**
 * 10. الحصول على تاريخ المعاملات للطلب
 */
export const getPaymentTransactions = async (maintenanceId: string): Promise<PaymentTransaction[]> => {
  try {
    const transactionsQuery = collection(db, 'paymentTransactions');
    const q = query(transactionsQuery, where('maintenanceId', '==', maintenanceId));
    const querySnapshot = await getDocs(q);

    const transactions: PaymentTransaction[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      transactions.push({
        id: doc.id,
        maintenanceId: data.maintenanceId,
        amount: data.amount,
        method: data.method,
        date: data.date instanceof Timestamp ? data.date.toDate() : new Date(data.date),
        notes: data.notes,
        createdBy: data.createdBy,
        createdByName: data.createdByName
      });
    });

    // ترتيب المعاملات حسب التاريخ (الأحدث أولاً)
    return transactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  } catch (error) {
    console.error('Error fetching payment transactions:', error);
    return [];
  }
};

/**
 * 5. تسجيل دفعة
 */
export const recordPayment = async (
  maintenanceId: string,
  amount: number,
  method: MaintenancePaymentMethod,
  userId: string,
  userName: string,
  notes?: string
): Promise<PaymentTransaction> => {
  try {
    // الحصول على بيانات الصيانة الحالية
    const maintenanceRef = doc(db, 'maintenance', maintenanceId);
    const maintenanceDoc = await getDoc(maintenanceRef);
    
    if (!maintenanceDoc.exists()) {
      throw new Error('Maintenance record not found');
    }
    
    const maintenanceData = maintenanceDoc.data();
    const currentPaid = maintenanceData.totalPaid || 0;
    const finalCost = maintenanceData.finalCost || maintenanceData.estimatedCost || maintenanceData.initialCost;
    const partsCost = maintenanceData.partsCost || 0;
    
    // حساب القيم الجديدة
    const newTotalPaid = currentPaid + amount;
    const newRemainingAmount = Math.max(0, finalCost - newTotalPaid);
    const hasDebt = newRemainingAmount > 0 && method === 'debt';
    
    // تحديد حالة الدفع
    let paymentStatus: PaymentStatus;
    if (newTotalPaid >= finalCost) {
      paymentStatus = 'fullyPaid';
    } else if (newTotalPaid > 0) {
      paymentStatus = 'partiallyPaid';
    } else {
      paymentStatus = 'unpaid';
    }
    
    // إنشاء سجل المعاملة
    const transaction: Omit<PaymentTransaction, 'id'> = {
      maintenanceId,
      amount,
      method,
      date: new Date(),
      notes,
      createdBy: userId,
      createdByName: userName
    };
    
    // حفظ المعاملة
    const transactionRef = await addDoc(collection(db, 'paymentTransactions'), {
      ...transaction,
      createdAt: Timestamp.now()
    });
    
    // تحديث سجل الصيانة
    const updateData: any = {
      totalPaid: newTotalPaid,
      remainingAmount: newRemainingAmount,
      paymentStatus,
      paymentMethod: method,
      paymentDate: Timestamp.now(),
      hasDebt,
      // تحديث الحقول القديمة للتوافق
      paid: newTotalPaid,
      technicianShare: Math.max(0, newTotalPaid - partsCost),
      updatedAt: Timestamp.now(),
      updatedBy: userId
    };
    
    // إذا كان هناك دين، تحديد تاريخ الاستحقاق
    if (hasDebt && newRemainingAmount > 0) {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 30); // 30 يوم من الآن
      updateData.debtDueDate = dueDate;
      updateData.debtAmount = newRemainingAmount;
    }
    
    await updateDoc(maintenanceRef, updateData);

    // إذا كانت الدفعة نقدية، تسجيلها في الخزينة كدفعة صيانة نقدية (منفصلة عن المبيعات)
    if (method === 'cash') {
      try {
        await addDoc(collection(db, 'drawerTransactions'), {
          type: 'maintenance_cash_payment',
          amount: amount, // موجب - يزيد الخزينة
          description: `دفعة صيانة نقدية - ${maintenanceData.customerName}`,
          maintenanceId,
          customerId: maintenanceData.customerId || '',
          customerName: maintenanceData.customerName || 'عميل غير محدد',
          employeeId: userId,
          employeeName: userName,
          branchId: maintenanceData.branchId || 'default',
          branchName: maintenanceData.branchName || 'الفرع الرئيسي',
          date: new Date().toISOString().split('T')[0],
          paymentDate: new Date(),
          notes: notes || `دفعة صيانة - ${maintenanceId.substring(0, 8)}`,
          createdAt: serverTimestamp()
        });
        console.log(`✅ تم تسجيل الدفعة النقدية في الخزينة كدفعة صيانة للصيانة ${maintenanceId}`);
      } catch (treasuryError) {
        console.error('❌ خطأ في تسجيل الدفعة في الخزينة:', treasuryError);
        // لا نريد إلغاء العملية إذا فشل تسجيل الخزينة
      }
    }

    console.log(`Payment of ${amount} recorded for maintenance ${maintenanceId}`);

    return {
      id: transactionRef.id,
      ...transaction
    };
  } catch (error) {
    console.error('Error recording payment:', error);
    throw error;
  }
};

/**
 * 6. إنشاء سجل دين إذا لزم الأمر
 */
export const createDebtRecord = async (
  maintenanceId: string,
  userId: string,
  forcedRemainingAmount?: number
): Promise<string | null> => {
  try {
    console.log('💳 بدء إنشاء سجل دين للصيانة:', maintenanceId);

    // الحصول على بيانات الصيانة
    const maintenanceRef = doc(db, 'maintenance', maintenanceId);
    const maintenanceDoc = await getDoc(maintenanceRef);

    if (!maintenanceDoc.exists()) {
      throw new Error('Maintenance record not found');
    }

    const maintenanceData = maintenanceDoc.data();

    // استخدام المبلغ المتبقي المُمرر أو حسابه من البيانات
    let remainingAmount: number;
    if (forcedRemainingAmount !== undefined) {
      remainingAmount = forcedRemainingAmount;
      console.log('💡 استخدام المبلغ المتبقي المُمرر:', remainingAmount);
    } else {
      // حساب المبلغ المتبقي من البيانات الحالية
      const finalCost = maintenanceData.finalCost || maintenanceData.estimatedCost || maintenanceData.initialCost || 0;
      const totalPaid = maintenanceData.totalPaid || maintenanceData.paid || 0;
      remainingAmount = Math.max(0, finalCost - totalPaid);
      console.log('🧮 حساب المبلغ المتبقي من البيانات:', {
        finalCost,
        totalPaid,
        remainingAmount
      });
    }

    console.log('📊 بيانات الصيانة للدين:', {
      maintenanceId,
      customerId: maintenanceData.customerId,
      customerName: maintenanceData.customerName,
      remainingAmount,
      orderNumber: maintenanceData.orderNumber,
      usedForcedAmount: forcedRemainingAmount !== undefined
    });

    // إذا لم يكن هناك مبلغ متبقي، لا حاجة لإنشاء دين
    if (remainingAmount <= 0) {
      console.log('ℹ️ لا يوجد مبلغ متبقي، لن يتم إنشاء دين');
      return null;
    }

    // استخدام خدمة الديون لإنشاء الدين بشكل صحيح
    const { createDebtFromMaintenance } = await import('./debtService');

    const debtDescription = `دين من طلب صيانة رقم ${maintenanceData.orderNumber} - ${maintenanceData.deviceType}`;
    const dueDate = maintenanceData.debtDueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

    const debt = await createDebtFromMaintenance(
      maintenanceId,
      maintenanceData.orderNumber || 0,
      maintenanceData.customerId,
      remainingAmount,
      debtDescription,
      userId,
      maintenanceData.technicianName || 'غير محدد',
      maintenanceData.branchId || '',
      maintenanceData.branchName || '',
      dueDate,
      `دين تلقائي من صيانة ${maintenanceData.deviceType} للعميل ${maintenanceData.customerName}`
    );

    // ربط الدين بسجل الصيانة
    await updateDoc(maintenanceRef, {
      debtId: debt.id,
      updatedAt: Timestamp.now()
    });

    console.log('✅ تم إنشاء سجل الدين بنجاح:', {
      debtId: debt.id,
      amount: remainingAmount,
      customerId: maintenanceData.customerId
    });

    return debt.id;
  } catch (error) {
    console.error('❌ خطأ في إنشاء سجل الدين:', error);
    throw error;
  }
};

/**
 * 7. حساب ملخص التكلفة والدفع
 */
export const calculateCostSummary = (maintenance: MaintenanceRecord) => {
  const initialCost = maintenance.initialCost || 0;
  const estimatedCost = maintenance.estimatedCost || initialCost;
  const finalCost = maintenance.finalCost || estimatedCost;
  
  // حساب تكلفة قطع الغيار من البيانات المحفوظة أو من قطع الغيار المستخدمة
  let partsCost = maintenance.partsCost || 0;
  
  // إذا لم يكن هناك تكلفة محفوظة، احسبها من قطع الغيار المستخدمة
  if (!partsCost && maintenance.usedParts && maintenance.usedParts.length > 0) {
    partsCost = maintenance.usedParts.reduce((sum: number, part: any) => {
      const sellingPrice = Number(part.sellingPrice) || Number(part.price) || 0;
      const quantity = Number(part.quantity) || 0;
      return sum + (sellingPrice * quantity);
    }, 0);
  }
  
  const laborCost = maintenance.laborCost || Math.max(0, finalCost - partsCost);
  const totalPaid = maintenance.totalPaid || maintenance.paid || 0;
  const remainingAmount = Math.max(0, finalCost - totalPaid);
  
  return {
    initialCost,
    estimatedCost,
    finalCost,
    partsCost,
    laborCost,
    totalPaid,
    remainingAmount,
    hasDebt: maintenance.hasDebt || false,
    paymentStatus: maintenance.paymentStatus,
    requiresCustomerApproval: maintenance.requireCallBeforeMaintenance && !maintenance.customerApproved
  };
};

/**
 * إضافة مدفوعة الصيانة إلى الخزينة
 */
const addMaintenancePaymentToTreasury = async (paymentData: {
  maintenanceId: string;
  amount: number;
  customerName: string;
  customerId: string;
  receivedBy: string;
  receivedByName: string;
  branchId: string;
  notes: string;
}) => {
  try {
    console.log('💰 بدء إضافة مدفوعة الصيانة إلى الخزينة:', paymentData);

    const firestore = ensureFirestore();
    const drawerCollection = collection(firestore, 'drawerTransactions');

    const transactionDoc = {
      type: 'maintenance_payment',
      amount: paymentData.amount,
      description: paymentData.notes,
      maintenanceId: paymentData.maintenanceId,
      customerId: paymentData.customerId,
      customerName: paymentData.customerName,
      employeeId: paymentData.receivedBy,
      employeeName: paymentData.receivedByName,
      branchId: paymentData.branchId,
      createdAt: serverTimestamp(),
      date: new Date().toISOString().split('T')[0] // تاريخ اليوم
    };

    console.log('📄 بيانات معاملة الخزينة التي سيتم حفظها:', transactionDoc);

    const docRef = await addDoc(drawerCollection, transactionDoc);

    console.log('✅ تم إضافة مدفوعة الصيانة إلى الخزينة بنجاح، ID:', docRef.id);
  } catch (error) {
    console.error('❌ خطأ في إضافة مدفوعة الصيانة إلى الخزينة:', error);

    // تفاصيل أكثر عن الخطأ
    if (error instanceof Error) {
      console.error('❌ رسالة الخطأ:', error.message);
      console.error('❌ تفاصيل الخطأ:', error.stack);
    }

    throw error;
  }
};
